/**
 * Device & runtime info hooks — surfaced on the customer Profile page so
 * users and QA can quickly identify the installed app version and verify
 * notification permission state when triaging push issues.
 *
 * Sources, in priority order:
 *
 *   App version + build number
 *     1. Capacitor `@capacitor/app` App.getInfo() — returns the *native*
 *        APK/IPA version & build number from AndroidManifest /
 *        Info.plist. Always the source of truth on a real device.
 *     2. Fallback: `process.env.REACT_APP_VERSION` (injected at web build
 *        time from package.json) + "web" build label.
 *
 *   Notification permission
 *     1. Capacitor PushNotifications.checkPermissions() — returns the
 *        actual native OS-level permission state.
 *     2. Fallback: Web `Notification.permission` (default | granted | denied).
 *     3. "unsupported" when neither runtime exposes notifications.
 */
import { useEffect, useState } from "react";

// Read directly from package.json — webpack lets us import JSON.
import pkg from "../../package.json";

function getCap() {
  if (typeof window === "undefined") return null;
  return window.Capacitor || null;
}

export function useAppVersion() {
  const [info, setInfo] = useState({
    version: process.env.REACT_APP_VERSION || pkg.version || "0.0.0",
    build: "",          // populated only on native
    source: "web",      // "web" | "native"
    ready: false,
  });

  useEffect(() => {
    let cancelled = false;
    const loadNative = async () => {
      const cap = getCap();
      const AppPlugin = cap?.Plugins?.App;
      if (!AppPlugin?.getInfo) {
        if (!cancelled) setInfo((s) => ({ ...s, ready: true }));
        return;
      }
      try {
        const native = await AppPlugin.getInfo();
        // App.getInfo() returns { name, id, version, build }
        if (!cancelled) {
          setInfo({
            version: native.version || pkg.version,
            build: native.build || "",
            source: "native",
            ready: true,
            appId: native.id,
            appName: native.name,
          });
        }
      } catch {
        if (!cancelled) setInfo((s) => ({ ...s, ready: true }));
      }
    };
    loadNative();
    return () => { cancelled = true; };
  }, []);

  return info;
}

export function useNotificationPermission() {
  // "enabled" | "disabled" | "prompt" | "unsupported" | "loading"
  const [state, setState] = useState({ status: "loading", source: null });

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      const cap = getCap();
      const plugin = cap?.Plugins?.PushNotifications;
      // 1. Native (Capacitor).
      if (plugin?.checkPermissions) {
        try {
          const r = await plugin.checkPermissions();
          if (cancelled) return;
          // r.receive: "granted" | "denied" | "prompt" | "prompt-with-rationale"
          const mapped =
            r.receive === "granted" ? "enabled" :
            r.receive === "denied" ? "disabled" :
            "prompt";
          setState({ status: mapped, source: "native" });
          return;
        } catch {
          /* fall through to web */
        }
      }
      // 2. Web Notifications API.
      if (typeof window !== "undefined" && "Notification" in window) {
        const p = window.Notification.permission;
        if (cancelled) return;
        setState({
          status: p === "granted" ? "enabled" : p === "denied" ? "disabled" : "prompt",
          source: "web",
        });
        return;
      }
      // 3. Nothing available.
      if (!cancelled) setState({ status: "unsupported", source: null });
    };
    check();
    return () => { cancelled = true; };
  }, []);

  return state;
}
